import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { PartnerService } from '../../../core/services/partner.service';
import { Partner } from '../../interfaces/partner.interface';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { NgOtpInputComponent } from 'ng-otp-input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

function binValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value || !/^\d{6}$|^\d{8}$/.test(value)) {
    return { invalidBin: true };
  }
  return null;
}

@Component({
  selector: 'app-registration-form',
  standalone: true,
  providers: [provideNgxMask()],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    NgxMaskDirective,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatCheckboxModule,
    MatButtonModule,
    NgOtpInputComponent,
    MatProgressSpinnerModule,
    MatSnackBarModule,
  ],
  templateUrl: './registration-form.component.html',
  styleUrl: './registration-form.component.scss',
})
export class RegistrationFormComponent implements OnInit, OnDestroy, OnChanges {
  @Input() partnerActive!: Partner;
  @Input() binActive = false;
  @Input() hasBackground = true;

  private readonly urPrivacy =
    'https://madero.blob.core.windows.net/fidelidade/Documentos/Politica_de_Privacidade_Madero_Cashback.pdf';
  private readonly urTerms =
    'https://madero.blob.core.windows.net/fidelidade/Documentos/Regulamento_Madero_Cashback.pdf';

  private timerInterval: any = null;
  step = 1;
  form: FormGroup;
  loading = false;
  error = '';
  otpError = false;
  resendTimer = 0;
  canResend = true;

  private generateDeviceId(): string {
    return 'WEB_' + Math.random().toString(36).substring(2, 15);
  }

  private showErrorSnackBar(message: string): void {
    this.snackBar.open(message, 'Fechar', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['error-snackbar'],
    });
  }

  constructor(
    private fb: FormBuilder,
    private partnerService: PartnerService,
    private snackBar: MatSnackBar
  ) {
    this.form = this.createForm();
  }

  private createForm(): FormGroup {
    return this.fb.group({
      phone: ['', [Validators.required]],
      code: ['', [Validators.required]],
      name: ['', [Validators.required, Validators.minLength(3)]],
      cpf: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      birthdate: ['', [Validators.required]],
      bin: [''],
      password: [''],
      companyName: [''],
      companyCode: [''],
      deviceId: [this.generateDeviceId()],
      accept: [false, Validators.requiredTrue],
      notifyEmail: [false],
      notifySms: [false],
      notifyWpp: [false],
      notifyAll: [false],
    });
  }

  ngOnInit(): void {
    this.onVerifyBIN();
    this.fillPartnerData();
    if (this.step === 2) {
      this.startResendTimer();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['partnerActive']) this.fillPartnerData();
    if (changes['binActive']) this.onVerifyBIN();
  }

  ngOnDestroy(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  onVerifyBIN(): void {
    const binCtrl = this.form.get('bin');
    if (!binCtrl) return;

    if (this.binActive) {
      binCtrl.setValidators([Validators.required, binValidator]);
    } else {
      binCtrl.clearValidators();
      binCtrl.setValue('');
    }

    binCtrl.updateValueAndValidity({ emitEvent: false });
  }


  private fillPartnerData(): void {
    if (this.partnerActive) {
      this.form.patchValue({
        companyCode: this.partnerActive.code || '',
        companyName: this.partnerActive.name || '',
      });
    }
  }

  // Step 1: Enviar código (register-lp)
  sendCode() {
    if (this.form.get('phone')?.invalid) return;
    if (!this.canResend) return;

    this.loading = true;
    this.error = '';

    this.partnerService.sendOtp(this.form.value.phone).subscribe({
      next: () => {
        this.step = 2;
        this.loading = false;
        this.startResendTimer();
      },
      error: (err) => {
        const errorMessage =
          err?.error?.errors[0]?.messageCustomer || 'Erro ao enviar código';
        this.error = errorMessage;
        this.showErrorSnackBar(errorMessage);
        this.loading = false;
      },
    });
  }

  // Step 2: Validar código (register-lp)
  validateCode() {
    if (this.form.get('code')?.invalid) return;

    this.loading = true;
    this.error = '';
    this.otpError = false;

    const otpInputs = document.querySelectorAll('.ng-otp-input-wrapper input');
    otpInputs.forEach((input: Element) => {
      (input as HTMLInputElement).blur();
    });    

    this.partnerService
      .verifyOtp(
        this.form.value.phone,
        this.form.value.code,
        this.form.value.companyCode
      )
      .subscribe({
        next: () => {
          this.step = 3;
          this.loading = false;
          this.otpError = false;
        },
        error: (err) => {
          const errorMessage =
            err?.error?.errors[0]?.messageCustomer || 'Código inválido';
          this.error = errorMessage;
          this.showErrorSnackBar(errorMessage);
          this.loading = false;
          this.otpError = true;
        },
      });
  }

  onOtpChange(otp: string) {
    if (otp && otp.length === 6) {
      this.form.get('code')?.setValue(otp);
      setTimeout(() => {
        this.validateCode();
      }, 100);
    }
  }

  clearOtpError() {
    this.otpError = false;
  }

  // Step 3: Dados pessoais (register-lp)
  submitPersonal() {
    if (this.form.invalid) return;

    this.loading = true;
    this.error = '';

    const formData = {
      cpf: this.form.value.cpf.replace(/\D/g, ''),
      name: this.form.value.name,
      phone: this.form.value.phone.replace(/\D/g, ''),
      email: this.form.value.email,
      birthdate: this.form.value.birthdate,
      companyCode: this.form.value.companyCode,
      password: this.form.value.bin ?? this.form.value.password,
      companyName: this.form.value.companyName,
      deviceId: this.form.value.deviceId,
      notifyEmail: this.form.value.notifyEmail,
      notifySms: this.form.value.notifySms,
      notifyWpp: this.form.value.notifyWpp,
    };

    this.partnerService.registerPartner(formData).subscribe({
      next: () => {
        this.step = 4;
        this.loading = false;
      },
      error: (err) => {
        const errorMessage =
          err?.error?.errors[0]?.messageCustomer || 'Erro ao cadastrar';
        this.error = errorMessage;
        this.showErrorSnackBar(errorMessage);
        this.loading = false;
      },
    });
  }

  toggleAllNotifications(checked: boolean): void {
    this.form.patchValue({
      notifyEmail: checked,
      notifySms: checked,
      notifyWpp: checked,
      notifyAll: checked,
    });
  }

  onNotifyPreferenceChange(): void {
    const notifyControls = ['notifyEmail', 'notifySms', 'notifyWpp'];
    const allChecked = notifyControls.every(
      (controlName) => this.form.get(controlName)?.value
    );
    this.form.get('notifyAll')?.setValue(allChecked, { emitEvent: false });
  }

  get f() {
    return this.form.controls;
  }

  onRedirect(nameLink: string): void {
    switch (nameLink) {
      case 'privacy':
        window.open(this.urPrivacy, '_blank');
        break;
      case 'terms':
        window.open(this.urTerms, '_blank');
        break;
      default:
        break;
    }
  }

  validateNumberInput(event: KeyboardEvent): void {
    const regex = /^[0-9]$/;
    const key = event.key;

    if (!regex.test(key)) {
      event.preventDefault();
    }
  }

  startResendTimer(): void {
    if (this.timerInterval) return;

    this.canResend = false;
    this.resendTimer = 60;

    this.timerInterval = setInterval(() => {
      this.resendTimer = Math.max(0, this.resendTimer - 1);
      if (this.resendTimer <= 0) {
        if (this.timerInterval) {
          clearInterval(this.timerInterval);
          this.timerInterval = null;
        }
        this.canResend = true;
      }
    }, 1000);
  }

  formatTimer(seconds: number): string {
    if (!seconds || seconds <= 0) return '00:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    const mm = String(m).padStart(2, '0');
    const ss = String(s).padStart(2, '0');
    return `${mm}:${ss}`;
  }
}
